import { useLayoutEffect, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Reset le scroll en haut de page au changement de route.
 *
 * Stratégie multi-fallback robuste (Chrome / Firefox / Safari iOS / Android Chrome) :
 *
 * 1) `history.scrollRestoration = 'manual'` — DÉSACTIVE la restauration auto du
 *    navigateur. Sans ça, Safari iOS (et parfois Chrome Android) restaure la
 *    position de scroll précédente APRÈS notre scrollTo(0,0), ce qui donne
 *    l'impression que ça ne fonctionne pas. C'est LA cause #1 du bug mobile.
 *
 * 2) `useLayoutEffect` — s'exécute APRÈS commit React mais AVANT paint navigateur.
 *    L'utilisateur ne voit jamais la nouvelle page à la mauvaise position.
 *
 * 3) On désactive temporairement `scroll-behavior: smooth` via inline style sur
 *    <html>, sinon le scroll programmatique hérite du CSS et prend 500ms+ sur
 *    les pages longues (le user a l'impression que ça ne remonte pas).
 *
 * 4) On scrolle window + documentElement + body (Safari iOS scroll parfois sur
 *    body, parfois sur documentElement).
 *
 * 5) Multi-frame scroll : 3 passages successifs (sync → rAF → rAF) car la
 *    nouvelle page (remontée par PageTransition via key={pathname}) peut
 *    changer la hauteur du document après le 1er scroll. iOS Safari clampe
 *    parfois le scrollTo si la hauteur n'est pas encore stable.
 *
 * Hash (#section) : on respecte la position de l'ancre, pas de reset.
 */
export function useScrollToTop() {
  const { pathname, hash } = useLocation();

  // 1) Désactive la scroll-restoration du navigateur (une fois au mount)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history) return;
    if ('scrollRestoration' in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
  }, []);

  // 2) Reset scroll à chaque changement de route
  useLayoutEffect(() => {
    if (hash) return; // ancre interne → laisse le navigateur scroller naturellement
    if (typeof window === 'undefined') return;

    const html = document.documentElement;
    const prevScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto'; // override CSS smooth temporairement

    const doScroll = () => {
      // Multi-target pour couvrir tous les navigateurs/conteneurs
      window.scrollTo(0, 0);
      if (html) html.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    };

    // Frame 1 : immédiat (avant paint)
    doScroll();

    // Frame 2 : après que React/PageTransition aient remplacé le DOM enfant
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      doScroll();
      // Frame 3 : après que le navigateur ait recalculé la hauteur du document
      // (critique sur iOS Safari quand la nouvelle page est plus longue)
      raf2 = window.requestAnimationFrame(() => {
        doScroll();
        html.style.scrollBehavior = prevScrollBehavior;
      });
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      // Sécurité : on rétablit toujours le scroll-behavior
      html.style.scrollBehavior = prevScrollBehavior;
    };
  }, [pathname, hash]);
}
